import React, { useEffect } from 'react'
import ProductTile from '../ProductTile'
import SpeedBumpTile from '../SpeedBumpTile'
import { GridWrapper, ViewAllLink, ContentWrapper, ButtonWrapper } from './ListingPreview.styles'
import { Spacing, Heading, Button } from '@big-red-group/storefront-common-ui'
import { PlpProduct, ListingPreviewViewMore } from '@/types'
import {
  getPoints,
  getRedeemPoints,
  getPointsBadgeIcon,
  getPointsBadgeStyles,
  getPointsDescription,
  getPointsStyles,
} from '@/helpers/LoyaltyCampaignHelper'
import { useRouter } from 'next/router'
import { useProductClicked, useProductListViewed } from '@/hooks/AnalyticsProductHook'
import { useLoyaltyCampaign } from '@/hooks/LoyaltyCampaignHook'
import { useAnalyticsMetaData } from '@/hooks/AnalyticsMetaDataHook'
import { RichSnippetContext } from '@/context/RichSnippetContext'
import { CANONICAL_BASE_URL } from '@/helpers/UrlHelper'

type ListingPreviewProps = {
  products: PlpProduct[]
  speedBumpTile: boolean
  isRecommended: boolean // Indicate if this listing preview widget is a recommendation list
  title?: string
  handlerKey?: string
  viewMore?: ListingPreviewViewMore // View more button label, view more handler
  smallMobileTiles?: boolean
  totalProductsCount?: number
  toggleProductListViewed?: boolean
}

const ListingPreview = ({
  title,
  viewMore,
  products,
  handlerKey,
  isRecommended,
  speedBumpTile = true,
  smallMobileTiles = false,
  totalProductsCount,
  toggleProductListViewed,
}: ListingPreviewProps) => {
  const loyaltyCampaign = useLoyaltyCampaign()
  const metadata = useAnalyticsMetaData()

  // TODO: Should come from constant
  const allowViewMore = products.length >= 12 && viewMore
  const router = useRouter()
  const lang = router.query.lang || 'en'

  // TODO: Figure out whether list ID and list name are available
  const trackProductListViewed = useProductListViewed(title)
  const trackProductClicked = useProductClicked()

  const selectSpeedBumpImage = (index: number) => {
    switch (index) {
      case 9:
        return 'speedbump-1'
      case 23:
        return 'speedbump-2'
      case 31:
        return 'speedbump-3'
      default:
        return ''
    }
  }
  // Disabled as apart of BAI-6059
  // const selectSpeedBumpImage = (index: number) => {
  //   switch (index) {
  //     case 9:
  //     case 23:
  //     case 31:
  //       return 'speedbump-promo'
  //     default:
  //       return ''
  //   }
  // }

  useEffect(() => {
    if (products.length && toggleProductListViewed) {
      const isPopularCategory = metadata.getIsPopularCategory()

      if (isPopularCategory) {
        trackProductListViewed(products, totalProductsCount, true, isPopularCategory)
        metadata.setIsPopularCategory(false)
      } else {
        trackProductListViewed(products, totalProductsCount, true)
      }
    }
  }, [products, trackProductListViewed, metadata, totalProductsCount, toggleProductListViewed])

  const { collect } = RichSnippetContext.useRichSnippetStructuredData()

  collect({
    type: 'Carousel',
    data: products.map(({ images, name, urlSegment, canonicalRegionUrlSegment }) => ({
      name,
      url: `${
        urlSegment.indexOf('http') >= 0
          ? urlSegment
          : `https://${CANONICAL_BASE_URL}/${lang}/${canonicalRegionUrlSegment}/${urlSegment}`
      }`,
      image: images?.[0],
    })),
  })

  return (
    <>
      {title && (
        <div>
          <ContentWrapper>
            <Heading as="h2" size="XL" spacing={false}>
              {title}
            </Heading>
            {allowViewMore && (
              <ViewAllLink onClick={() => (viewMore ? router.push(viewMore?.link) : () => {})}>
                <Heading as="div" size="XS" spacing={false} headingGraphic="stroke">
                  View all <span>&amp; filter</span>
                </Heading>
              </ViewAllLink>
            )}
          </ContentWrapper>
          <Spacing size="XL" />
        </div>
      )}
      <GridWrapper smallMobileTiles={smallMobileTiles} className="listing-grid">
        {products.map(
          (
            {
              id,
              urlSegment,
              images,
              retailPrice,
              operatorName,
              name,
              fromPrice,
              region,
              levy,
              rating,
              primaryRegion,
              canonicalRegionUrlSegment,
              decorators,
              hotDeals,
              geolocations,
              summary,
            },
            index
          ) => {
            const marker = index + 1
            return (
              <React.Fragment key={index}>
                {/* TODO review url */}
                {(marker === 9 || marker === 23 || marker === 31) && speedBumpTile && (
                  <>
                    <SpeedBumpTile image={selectSpeedBumpImage(marker)} key={`speedbump-${index}`} />
                  </>
                )}
                <ProductTile
                  smallMobileTile={smallMobileTiles}
                  badges={decorators}
                  hotDeals={hotDeals}
                  id={id}
                  key={`product-${index}`}
                  image={images?.[0]}
                  price={retailPrice}
                  canonicalRegionUrlSegment={canonicalRegionUrlSegment}
                  region={region}
                  supplier={operatorName}
                  title={name}
                  url={`/${lang}/${canonicalRegionUrlSegment || primaryRegion}/${urlSegment}`}
                  fromPrice={fromPrice}
                  levy={levy}
                  rating={rating}
                  geolocations={geolocations}
                  summary={summary}
                  pointsProps={
                    loyaltyCampaign
                      ? {
                          points: getRedeemPoints(fromPrice)!,
                          description: getPointsDescription(handlerKey),
                          pointsStyles: getPointsStyles(handlerKey),
                        }
                      : undefined
                  }
                  pointsBadgeProps={
                    loyaltyCampaign
                      ? {
                          icon: getPointsBadgeIcon(handlerKey),
                          points: getPoints(fromPrice, loyaltyCampaign)!,
                          description: getPointsDescription(handlerKey),
                          style: getPointsBadgeStyles(handlerKey),
                        }
                      : undefined
                  }
                  onClick={() =>
                    trackProductClicked({
                      product: products[index],
                      position: index + 1,
                      isRecommended,
                      fromWidget: true,
                      listTitle: title,
                    })
                  }
                />
              </React.Fragment>
            )
          }
        )}
      </GridWrapper>
      {viewMore && allowViewMore && (
        <>
          <Spacing size="L" />
          <ButtonWrapper>
            <Button alternate onClick={() => router.push(viewMore?.link)}>
              {viewMore.label}
            </Button>
          </ButtonWrapper>
        </>
      )}
    </>
  )
}

export default ListingPreview
